import { ReactElement, useCallback, useMemo, useState } from 'react'
import { elementTypeToLabelSingular } from '@domainConstants'
import { useModelClickListenerV2 } from '@scene'
import { Search } from '@mui/icons-material'
import { Button, Divider, Popover, Stack, TextField, Typography } from '@mui/material'
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view-pro'
import { ThreeEvent } from '@react-three/fiber'
import { FocusIconButton, ToggleButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { Box } from '@ui/structure'
import {
  useElementLabel,
  useElementPropertiesTreeInformation,
  useElementType,
  useGuidToElementIncludingRips,
  useModelClickListeners,
  useSelectionMode,
  resolveFocusGuidsForElement,
} from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { useControlStore } from '../../../stores/controlStore'
import { useModelStore } from '../../../stores/modelStore'
import { useStiffeningStore } from '../../../stores/stiffeningStore'

const selectionKey = 'select-component-detail'

const ComponentDetailControls = (): ReactElement => {
  const [selectedElement, setSelectedElement] = useState<string | null>(null)

  const actionMode = useControlStore(state => state.actionMode)

  const componentDetailEnabled = useControlStore(state => state.componentDetailEnabled)
  const toggleComponentDetailEnabled = useControlStore(state => state.toggleComponentDetailEnabled)
  const focusElements = useModelStore(state => state.focusElements)

  const { enqueueSnackbar } = useSnackbar()

  const { isSelectionMode, setSelectionMode, unsetSelectionMode, selectionModeKey } =
    useSelectionMode()

  const {
    actions: { setElements, toggleMode },
  } = useStructuralPlanningSelection()

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const guidToElement = useGuidToElementIncludingRips()
  const guidToLabel = useElementLabel()
  const stiffeningIntervals = useStiffeningStore(state => state.stiffeningIntervals)

  const availableGuids = useMemo(() => {
    const elementGuids = Object.keys(guidToElement)
    const stiffeningGuids = stiffeningIntervals
      .map(({ guid }) => guid)
      .filter((g): g is string => typeof g === 'string' && g.length > 0)
    return [...new Set([...elementGuids, ...stiffeningGuids])]
  }, [guidToElement, stiffeningIntervals])

  const labelToGuid = useMemo(() => {
    const mapping: { [label: string]: string } = {}
    availableGuids.forEach(guid => {
      const label = guidToLabel(guid)
      if (label) {
        mapping[label] = guid
      }
    })
    return mapping
  }, [availableGuids, guidToLabel])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setAnchorEl(event.currentTarget)
      toggleComponentDetailEnabled()
    },
    [setAnchorEl, toggleComponentDetailEnabled],
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
    toggleComponentDetailEnabled()
  }, [setAnchorEl, toggleComponentDetailEnabled])

  const selectElement = useCallback(
    (elementGuid: string) => {
      setSelectedElement(elementGuid)
      unsetSelectionMode()
      toggleComponentDetailEnabled()
    },
    [unsetSelectionMode, toggleComponentDetailEnabled],
  )

  const selectElementEvent: ModelClickListener = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      selectElement(event.object.name)
    },
    [selectElement],
  )

  useModelClickListeners(
    selectElementEvent,
    [],
    selectionModeKey === selectionKey && isSelectionMode,
  )

  useModelClickListenerV2('position', selectElement, {
    enabled: selectionModeKey === selectionKey && isSelectionMode && actionMode !== 'hide',
    guids: null,
  })

  const information = useElementPropertiesTreeInformation(selectedElement)

  const elementType = useElementType(selectedElement)

  // TREE VIEW SELECTION

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const toTree = (data: any, depth: string): ReactElement[] => {
    if (!data) return []

    return Object.entries(data).map(([key, val], index) => {
      const nodeId = `${depth}-${index}`

      if (typeof val === 'object') {
        return (
          <TreeItem key={nodeId} itemId={nodeId} label={key}>
            {toTree(val, nodeId)}
          </TreeItem>
        )
      }

      return (
        <TreeItem
          key={nodeId}
          itemId={nodeId}
          label={`${key}: ${val}`}
          onClick={() => {
            const content = val?.toString() || ''
            navigator.clipboard.writeText(content.replace('.', ','))
            enqueueSnackbar('Wert wurde in die Zwischenablage kopiert', { variant: 'info' })
          }}
        />
      )
    })
  }

  return (
    <Box>
      <ToggleButton
        hint="Eigenschaften anzeigen"
        onClick={handleClick}
        selected={componentDetailEnabled}
        Icon={Search}
      />
      <Popover
        open={componentDetailEnabled}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            padding: 2,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Stack direction="column" spacing={3}>
            <Button
              onClick={() => {
                setSelectionMode({
                  key: selectionKey,
                  message: 'Wählen Sie ein Bauteil aus um Informationen zu erhalten',
                })
                toggleComponentDetailEnabled()
              }}
              variant="contained"
              size="small"
            >
              Element Anwählen
            </Button>
            <TextField
              label="Element suchen"
              placeholder="Element suchen"
              onChange={event => {
                const guidOrLabel = event.target.value
                const guid = availableGuids.includes(guidOrLabel)
                  ? guidOrLabel
                  : labelToGuid[guidOrLabel]
                setSelectedElement(guid || null)
              }}
              size="small"
            ></TextField>

            {information ? (
              <>
                <Divider />
                <Box>
                  {elementType && (
                    <Typography variant="h5" mb={2}>
                      {elementTypeToLabelSingular[elementType]}
                    </Typography>
                  )}

                  {information && (
                    <SimpleTreeView
                      sx={{ height: 240, flexGrow: 1, width: 400, overflowY: 'auto' }}
                    >
                      {toTree(information, 'root')}
                    </SimpleTreeView>
                  )}
                </Box>
                {selectedElement && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FocusIconButton
                      hint="Element isolieren (Transparenzmodus)"
                      onClick={() =>
                        focusElements(
                          resolveFocusGuidsForElement(selectedElement, stiffeningIntervals),
                        )
                      }
                    />
                    <Button
                      onClick={() => {
                        toggleMode(undefined)
                        setElements([selectedElement])
                      }}
                    >
                      Anzeigen (Erfordert Tragwerksplanung Modus)
                    </Button>
                  </Stack>
                )}
              </>
            ) : (
              <Typography>Kein Element angewählt</Typography>
            )}
          </Stack>
        </Box>
      </Popover>
    </Box>
  )
}

export default ComponentDetailControls
